<script setup lang="ts">
  import { ref, computed, watch } from 'vue'

  interface IDrawerComponentProps {
    // whether the panel should start open
    isOpen?: boolean
    // width/height can be number (interpreted as px) or string (any CSS unit)
    width?: number | string
    height?: number | string
    // distance from bottom of viewport (number => px or any CSS unit string)
    bottomOffset?: number | string
    class?: string
    // height of the toggle/tab button (number => px or any CSS unit string)
    toggleButtonHeight?: number | string
  }

  const props = withDefaults(defineProps<IDrawerComponentProps>(), {
    isOpen: false,
    width: 400,
    height: 250,
    // default distance from bottom as percentage
    bottomOffset: '10%',
    toggleButtonHeight: 120,
  })

  const open = ref(!!props.isOpen)
  watch(
    () => props.isOpen,
    (v) => (open.value = !!v)
  )

  const formatDim = (d: number | string) => (typeof d === 'number' ? `${d}px` : `${d}`)

  // visibility control: show before open animation, hide after close animation completes
  const ANIM_MS = 280 // match CSS transform transition duration
  const visibility = ref<'visible' | 'hidden'>(open.value ? 'visible' : 'hidden')
  let hideTimer: ReturnType<typeof setTimeout> | null = null

  watch(open, (val) => {
    if (hideTimer) {
      clearTimeout(hideTimer)
      hideTimer = null
    }
    if (val) {
      // ensure visible before animation begins
      visibility.value = 'visible'
    } else {
      // wait for transform animation to finish, then hide
      hideTimer = setTimeout(() => {
        visibility.value = 'hidden'
        hideTimer = null
      }, ANIM_MS)
    }
  })

  // panel style: include visibility (controlled above) and keep transform transitions
  const panelStyle = computed(() => ({
    width: formatDim(props.width),
    height: formatDim(props.height),
    transform: open.value ? 'translateX(0)' : `translateX(calc(100%))`,
    visibility: visibility.value
  }))

  const bottom = computed(() => formatDim(props.bottomOffset))
  const toggleHeight = computed(() => formatDim(props.toggleButtonHeight))
</script>

<template>
  <!-- fixed container anchored to right and positioned from top via topOffset -->
  <div class="custom-drawer" :style="{ height: panelStyle.height, bottom: bottom, pointerEvents: open ? 'auto' : 'none' }">
    <!-- panel -->
    <div
      class="drawer-panel rounded-lg opacity-80 border bg-[var(--card-lighter)] text-card-foreground shadow-sm dark:bg-[var(--card)]"
      :style="panelStyle"
    >
      <div class="drawer-content">
        <slot>
          <!-- default content -->
          <div style="padding: 16px">Heavy controls or content that should sit above app</div>
        </slot>
      </div>
    </div>

    <!-- tab sits to the left of the panel (visible when closed & open) -->
    <button
      class="drawer-tab"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
      :style="{ height: toggleHeight }"
      aria-label="Toggle drawer"
    >
      <!-- chevron -->
      <svg
        class="chev"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
  .custom-drawer {
    position: fixed;
    right: 0;
    /* top is set via inline style from topOffset */
    display: flex;
    align-items: center;
    z-index: 45;
    /* pointer-events toggled via inline style based on open state */
  }

  /* the sliding panel */
  .drawer-panel {
    position: relative;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    border-radius: 8px 0 0 8px;
    overflow: hidden;
    transition: transform 280ms ease;
    transform: translateX(
      calc(100% - 36px)
    ); /* initial closed position; overridden by inline style */
    z-index: 1000; /* ensure panel is below the tab */
  }

  /* content area inside panel */
  .drawer-content {
    width: 100%;
    height: 100%;
    overflow: auto;
  }

  /* the vertical tab — always interactive so the user can open the drawer */
  .drawer-tab {
    pointer-events: auto;
    width: 36px;
    /* height now controlled via inline style (toggleButtonHeight) */
    padding: 0;
    border: none;
    background: #001529;
    color: #fff;
    border-radius: 8px 0 0 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    position: relative;
    z-index: 1001; /* keep tab above the panel so it's always visible */
    margin-left: -36px; /* pull the tab left so it sits to the left of the panel */
    pointer-events: auto;
  }

  /* rotate the chevron when open */
  .drawer-tab .chev {
    transition: transform 220ms ease;
    transform: rotate(180deg);
  }

  /* explicit selector for open state */
  .drawer-tab[aria-expanded='true'] .chev {
    transform: rotate(0deg);
  }

  /* small responsive tweak */
  @media (max-width: 480px) {
    .drawer-tab {
      height: 80px;
    }
  }
</style>
